import { DragEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from "lucide-react";
import { IconButton } from "@doculegis/shared";
import { ChatMessageBubble } from "./ChatMessageBubble";
import { AttachedSectionChip } from "./AttachedSectionChip";
import { chatSendButtonClassName } from "./chat-controls";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { apiUrl } from "@/lib/api-config";
import {
  MAX_ATTACHED_SECTIONS,
  REVIEW_SECTION_MIME,
  parseAttachedSections,
  reviewRowDedupeKey,
  serializeAttachedSections,
} from "@/lib/chat-attachments";
import type { ChatMessage, ReviewRecord, ReviewReport, ReviewRow } from "@/types";

/** Pixels from bottom to consider the user "following" new streamed content. */
const SCROLL_BOTTOM_THRESHOLD_PX = 48;

function isNearBottom(el: HTMLElement): boolean {
  const { scrollTop, scrollHeight, clientHeight } = el;
  const noScrollNeeded = scrollHeight <= clientHeight + 2;
  return (
    noScrollNeeded || scrollTop + clientHeight >= scrollHeight - SCROLL_BOTTOM_THRESHOLD_PX
  );
}

interface ReviewChatPanelProps {
  review: ReviewRecord;
  className?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  /** Pointer handlers for dragging the floating panel by its header (mobile). */
  headerDragHandlers?: {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
  };
}

function generateId(): string {
  return `review-msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function toReviewReport(review: ReviewRecord): ReviewReport {
  return {
    summary: review.summary || "",
    reviewTable: review.review_table || [],
    overallRiskScore: review.overall_risk_score || 0,
    recommendations: review.recommendations || [],
  };
}

interface DbChatMessage {
  id: string;
  role: string;
  content: string;
  created_at: string;
}

function dbMessageToChatMessage(dbMsg: DbChatMessage): ChatMessage {
  const { sections, cleanText } = parseAttachedSections(dbMsg.content);
  return {
    id: dbMsg.id,
    role: dbMsg.role as "user" | "assistant",
    parts: [{ type: "text", text: cleanText }],
    createdAt: dbMsg.created_at,
    ...(sections.length > 0 ? { attachedSections: sections } : {}),
  };
}

export function ReviewChatPanel({
  review,
  className,
  isCollapsed = false,
  onToggleCollapse,
  onClose,
  onToggleFullscreen,
  isFullscreen = false,
  headerDragHandlers,
}: ReviewChatPanelProps) {
  const { getAccessToken } = useAuth();
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachedSections, setAttachedSections] = useState<ReviewRow[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [attachmentCapNotice, setAttachmentCapNotice] = useState<string | null>(null);
  /** Browser timer id (avoid NodeJS.Timeout vs number mismatch under @types/node). */
  const capNoticeTimeoutRef = useRef<number | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  /** When false, streamed chunks do not snap the viewport (user scrolled up to read). */
  const shouldStickToBottomRef = useRef(true);
  /** Ignore scroll events caused by our own scrollIntoView (layout uses block:start, not scroll-to-bottom). */
  const isProgrammaticScrollRef = useRef(false);
  /** Last scrollTop for detecting user scrolling up vs programmatic jumps. */
  const prevScrollTopRef = useRef(0);
  const lastMessageAnchorRef = useRef<HTMLDivElement>(null);

  const showAttachmentCapNotice = useCallback((message: string) => {
    setAttachmentCapNotice(message);
    if (capNoticeTimeoutRef.current != null) window.clearTimeout(capNoticeTimeoutRef.current);
    capNoticeTimeoutRef.current = window.setTimeout(() => {
      setAttachmentCapNotice(null);
      capNoticeTimeoutRef.current = null;
    }, 4000) as unknown as number;
  }, []);

  useEffect(() => {
    return () => {
      if (capNoticeTimeoutRef.current != null) window.clearTimeout(capNoticeTimeoutRef.current);
    };
  }, []);

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (isCollapsed) return;
      if (!event.dataTransfer.types.includes(REVIEW_SECTION_MIME)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      setIsDragOver(true);
    },
    [isCollapsed],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      if (isCollapsed) return;
      event.preventDefault();
      setIsDragOver(false);
      const raw = event.dataTransfer.getData(REVIEW_SECTION_MIME);
      if (!raw) return;
      let row: ReviewRow;
      try {
        row = JSON.parse(raw) as ReviewRow;
      } catch {
        return;
      }
      setAttachedSections(prev => {
        const key = reviewRowDedupeKey(row);
        if (prev.some(r => reviewRowDedupeKey(r) === key)) return prev;
        if (prev.length >= MAX_ATTACHED_SECTIONS) {
          showAttachmentCapNotice(
            `You can attach up to ${MAX_ATTACHED_SECTIONS} sections per message. Remove one to add another.`,
          );
          return prev;
        }
        return [...prev, row];
      });
    },
    [showAttachmentCapNotice, isCollapsed],
  );

  const handleMessagesScroll = useCallback(() => {
    if (isProgrammaticScrollRef.current) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    const { scrollTop } = el;
    const delta = scrollTop - prevScrollTopRef.current;
    // User moved away from the bottom (up or down) → stop auto-snapping until they return.
    if (Math.abs(delta) > 4 && !isNearBottom(el)) {
      shouldStickToBottomRef.current = false;
    } else if (isNearBottom(el)) {
      // User returned to the bottom → resume following the stream.
      shouldStickToBottomRef.current = true;
    }
    prevScrollTopRef.current = scrollTop;
  }, []);

  /** Align the top of the latest message with the top of the chat scroll area (not the thread bottom). */
  const scrollLastMessageIntoView = useCallback(() => {
    lastMessageAnchorRef.current?.scrollIntoView({
      block: "start",
      behavior: "auto",
    });
  }, []);

  /** Only toggles when stream has text vs empty — avoids re-anchoring on every token. */
  const hasStreamingText = streamingText.length > 0;

  useEffect(() => {
    if (messages.length === 0 && !isStreaming) return;
    if (!shouldStickToBottomRef.current) return;

    isProgrammaticScrollRef.current = true;
    scrollLastMessageIntoView();
    const t = window.setTimeout(() => {
      scrollLastMessageIntoView();
      window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        const el = messagesContainerRef.current;
        if (el) prevScrollTopRef.current = el.scrollTop;
      }, 0);
    }, 0);
    return () => clearTimeout(t);
  }, [messages, isStreaming, hasStreamingText, scrollLastMessageIntoView]);

  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        const accessToken = await getAccessToken();
        const response = await fetch(apiUrl(`/api/chat/history/${review.id}`), {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });

        if (!response.ok) return;

        const data: DbChatMessage[] = await response.json();
        const chatMessages = data.map(dbMessageToChatMessage);
        setMessages(chatMessages);
      } catch (error) {
        console.error("Error loading chat history:", error);
      }
    };

    void loadChatHistory();
  }, [getAccessToken, review.id]);

  const reportContext = toReviewReport(review);
  const formData = {
    contractType: review.contract_type || "",
    jurisdiction: review.jurisdiction || "",
    userRole: review.user_role || "",
    backgroundInfo: review.background_info || "",
  };

  const sendMessage = useCallback(
    async (text: string, attachedForSend: ReviewRow[]) => {
      const trimmed = text.trim();
      const userVisible =
        trimmed ||
        (attachedForSend.length > 0
          ? "Please focus on the attached section(s)."
          : "");
      if (!userVisible && attachedForSend.length === 0) return;

      const persistedUserText =
        serializeAttachedSections(attachedForSend) + userVisible;

      const userMessage: ChatMessage = {
        id: generateId(),
        role: "user",
        parts: [{ type: "text", text: userVisible }],
        createdAt: new Date().toISOString(),
        ...(attachedForSend.length > 0 ? { attachedSections: attachedForSend } : {}),
      };

      const messageForApi: ChatMessage = {
        id: userMessage.id,
        role: "user",
        parts: [{ type: "text", text: persistedUserText }],
        createdAt: userMessage.createdAt,
      };

      shouldStickToBottomRef.current = true;

      const nextMessages = [...messages, userMessage];
      const nextMessagesForApi = [...messages, messageForApi];
      setMessages(nextMessages);
      setIsStreaming(true);
      setStreamingText("");

      try {
        const accessToken = await getAccessToken();
        const response = await fetch(apiUrl("/api/chat"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            messages: nextMessagesForApi,
            reportContext,
            contractContext: review.contract_text,
            formData,
            reviewId: review.id,
            attachedSections: attachedForSend,
          }),
        });

        if (!response.ok) {
          let detail = "Failed to send message";
          try {
            const errBody = (await response.json()) as { error?: string };
            if (errBody.error) detail = errBody.error;
          } catch {
            // ignore parse errors
          }
          throw new Error(detail);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        const decoder = new TextDecoder();
        let accumulatedText = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const data = JSON.parse(line.substring(6));
              if (data.type === "text") {
                accumulatedText += data.text;
                setStreamingText(accumulatedText);
              } else if (data.type === "error") {
                throw new Error(typeof data.error === "string" ? data.error : "Stream error");
              } else if (data.type === "done") {
                const assistantMessage: ChatMessage = {
                  id: generateId(),
                  role: "assistant",
                  parts: [{ type: "text", text: accumulatedText }],
                  createdAt: new Date().toISOString(),
                };
                setMessages(current => [...current, assistantMessage]);
                setStreamingText("");
              }
            } catch (err) {
              if (err instanceof SyntaxError) continue;
              throw err;
            }
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);
        setMessages(current => [
          ...current,
          {
            id: generateId(),
            role: "assistant",
            parts: [
              {
                type: "text",
                text:
                  error instanceof Error
                    ? error.message
                    : "Sorry, there was an error processing your request. Please try again.",
              },
            ],
            createdAt: new Date().toISOString(),
          },
        ]);
      } finally {
        setIsStreaming(false);
      }
    },
    [formData, getAccessToken, messages, reportContext, review.contract_text, review.id]
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isStreaming) return;
    if (!input.trim() && attachedSections.length === 0) return;
    const snapshot = [...attachedSections];
    void sendMessage(input, snapshot);
    setInput("");
    setAttachedSections([]);
  };

  const canSubmit = Boolean(input.trim() || attachedSections.length > 0);

  return (
    <div
      className={cn(
        "relative flex h-full w-full flex-col overflow-hidden rounded-2xl border border-[rgba(15,23,42,0.09)] bg-[var(--chat-shell-bg)] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-gradient-to-b dark:from-slate-800 dark:to-slate-900 dark:shadow-none",
        !isCollapsed && isDragOver && "ring-2 ring-violet-500/60 ring-offset-2 ring-offset-white dark:ring-offset-slate-900",
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {!isCollapsed && isDragOver ? (
        <div
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl border-2 border-dashed border-violet-400/70 bg-slate-950/55 backdrop-blur-[2px]"
          aria-hidden
        >
          <p className="rounded-lg bg-slate-900/90 px-4 py-2 text-sm font-medium text-violet-200 shadow-lg">
            Drop section to attach
          </p>
        </div>
      ) : null}
      <div
        className={cn(
          "flex border-b border-[rgba(15,23,42,0.09)] bg-gradient-to-r from-blue-600/[0.08] to-purple-600/[0.08] dark:border-white/10 dark:from-blue-600/20 dark:to-purple-600/20",
          isCollapsed
            ? "h-full flex-col items-center justify-between py-4 px-2"
            : "items-center justify-between px-4 py-3",
          headerDragHandlers && "cursor-grab touch-none active:cursor-grabbing",
        )}
        {...(headerDragHandlers ?? {})}
      >
        {isCollapsed ? (
          <>
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/20">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            {onToggleCollapse ? (
              <IconButton
                type="button"
                size="sm"
                onClick={onToggleCollapse}
                title="Expand chat"
                aria-label="Expand chat"
              >
                <ChevronLeft className="h-4 w-4" />
              </IconButton>
            ) : null}
          </>
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/20">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold text-[#101828] dark:text-white">Contract Assistant</h3>
                {!headerDragHandlers ? (
                  <p className="text-xs text-[#576274] dark:text-gray-400">
                    Drag table rows here or ask about this review
                  </p>
                ) : null}
              </div>
            </div>
            <div className="ml-2 flex shrink-0 items-center gap-1.5">
              {onToggleFullscreen ? (
                <IconButton
                  type="button"
                  size="sm"
                  onClick={onToggleFullscreen}
                  title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-4 w-4" />
                  ) : (
                    <Maximize2 className="h-4 w-4" />
                  )}
                </IconButton>
              ) : null}
              {onToggleCollapse ? (
                <IconButton
                  type="button"
                  size="sm"
                  onClick={onToggleCollapse}
                  title="Minimize chat"
                  aria-label="Minimize chat"
                >
                  <ChevronRight className="h-4 w-4" />
                </IconButton>
              ) : null}
              {onClose ? (
                <IconButton
                  type="button"
                  size="sm"
                  onClick={onClose}
                  title="Close chat"
                  aria-label="Close chat"
                >
                  <X className="h-4 w-4" />
                </IconButton>
              ) : null}
            </div>
          </>
        )}
      </div>

      {!isCollapsed && messages.length === 0 ? (
        <div className="border-b border-[rgba(15,23,42,0.09)] px-5 py-5 dark:border-white/5">
          <p className="mb-3 text-sm text-[#576274] dark:text-gray-300">
            Ask me anything about this contract review, risk findings, and recommended changes.
          </p>
          <div className="space-y-2">
            {[
              "What are the highest risk sections?",
              "Summarize negotiation priorities",
              "Explain the governing law impact",
            ].map(prompt => (
              <button
                key={prompt}
                onClick={() => setInput(prompt)}
                className="block w-full rounded-lg border border-[rgba(15,23,42,0.09)] bg-white px-3 py-2 text-left text-xs text-[#576274] shadow-[var(--shadow-card)] transition-colors hover:bg-[var(--lm-hover)] hover:text-[#101828] dark:border-white/5 dark:bg-white/5 dark:text-gray-400 dark:shadow-none dark:hover:bg-white/10"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {!isCollapsed ? (
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-none"
      >
        <div className="space-y-4 p-4">
          {messages.map((message, index) => {
            const isLastPersisted =
              index === messages.length - 1 &&
              !(isStreaming && streamingText) &&
              !(isStreaming && !streamingText);
            return (
              <div
                key={message.id}
                ref={isLastPersisted ? lastMessageAnchorRef : undefined}
                className={isLastPersisted ? "scroll-mt-6" : undefined}
              >
                <ChatMessageBubble message={message} />
              </div>
            );
          })}
          {isStreaming && streamingText ? (
            <div ref={lastMessageAnchorRef} className="scroll-mt-6">
              <ChatMessageBubble
                message={{
                  id: "streaming",
                  role: "assistant",
                  parts: [{ type: "text", text: streamingText }],
                }}
              />
            </div>
          ) : null}
          {isStreaming && !streamingText ? (
            <div
              ref={lastMessageAnchorRef}
              className="group flex scroll-mt-6 flex-row gap-2.5"
            >
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border bg-[var(--avatar-assistant-bg)] border-[var(--avatar-assistant-border)] dark:bg-gradient-to-br dark:from-blue-500 dark:to-purple-600 dark:border-transparent">
                <Sparkles className="h-4 w-4 text-[var(--avatar-assistant-fg)] dark:text-white" />
              </div>
              <div className="relative max-w-[84%] rounded-2xl rounded-tl-sm border border-[rgba(15,23,42,0.09)] bg-white px-4 py-3 text-sm text-[#101828] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-white/10 dark:text-white dark:shadow-none">
                <div className="flex items-center gap-2 text-[#576274] dark:text-gray-300">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      ) : null}

      {!isCollapsed ? (
      <form onSubmit={handleSubmit} className="border-t border-[rgba(15,23,42,0.09)] bg-[var(--composer-bar-bg)] p-4 backdrop-blur-sm dark:border-white/10 dark:bg-slate-900/80">
        {attachedSections.length > 0 ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {attachedSections.map(row => (
              <AttachedSectionChip
                key={reviewRowDedupeKey(row)}
                row={row}
                onRemove={() =>
                  setAttachedSections(prev =>
                    prev.filter(r => reviewRowDedupeKey(r) !== reviewRowDedupeKey(row)),
                  )
                }
              />
            ))}
          </div>
        ) : null}
        {attachmentCapNotice ? (
          <p className="mb-2 text-xs text-amber-300/90">{attachmentCapNotice}</p>
        ) : null}
        <div className="flex items-end gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={input}
              onChange={event => setInput(event.target.value)}
              placeholder="Type your message..."
              disabled={isStreaming}
              className={cn(
                "w-full rounded-xl border border-[rgba(15,23,42,0.09)] bg-[var(--composer-bg)] px-4 py-3 text-sm text-[#101828] placeholder-[#576274] shadow-[var(--shadow-card)] dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder-gray-500 dark:shadow-none",
                "focus:border-blue-500/50 focus:outline-none focus:ring-2 focus:ring-blue-500/20",
                "disabled:cursor-not-allowed disabled:opacity-50 transition-all"
              )}
            />
          </div>
          <button
            type="submit"
            disabled={isStreaming || !canSubmit}
            className={chatSendButtonClassName(canSubmit && !isStreaming)}
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </form>
      ) : null}
    </div>
  );
}
